import { Download } from '@mui/icons-material'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'
import { Typography, Stack, IconButton, Chip, Tooltip } from '@mui/material'

import dayjs from 'dayjs'
import 'dayjs/locale/pt-br'
import PropTypes from 'prop-types'
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table'
import { MRT_Localization_PT_BR } from 'material-react-table/locales/pt-BR'
import { useState, useEffect, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { acDownloadBatchFile, acGetAllBatchFiles, acSetBatchFilesTablePagination } from '../../store/actions'
import { triggerBrowserDownload } from '../../store/actions/ActionUtil'

const statusMap = {
    'success': { color: 'success', message: 'sucesso' },
    'error': { color: 'error', message: 'erro' },
    'canceled': { color: 'warning', message: 'cancelado' },
    'processing': { color: 'info', message: 'processando' },
    'parsed': { color: 'info', message: 'parseado' },
    'uploading': { color: 'info', message: 'upload' }
}

const BatchFilesTable = ({ setSnackbarData }) => {

    const dispatch = useDispatch()

    const [hasBatchFiles, setHasBatchFiles] = useState(false)

    const {
        rowCount, pagination,

        allBatchFiles,
        isLoadingAllBatchFiles,
        hasErrorAllBatchFiles,

        isUploadingBatchFile,

        isDownloadingBatchFile,
        //hasErrorDownloadedBatchFile
    } = useSelector(state => state.batchFiles)

    useEffect(() => {
        dispatch(acGetAllBatchFiles({ pagination }))
    }, [pagination])

    useEffect(() => {
        if (allBatchFiles?.length > 0 && !hasBatchFiles) {
            setHasBatchFiles(true)
        }
    }, [allBatchFiles])

    const handleDownload = (batchFileId, name) => {
        if (isDownloadingBatchFile) return

        dispatch(acDownloadBatchFile(batchFileId, 'output'))
            .then(blob => {
                triggerBrowserDownload(blob, name)
            })
            .catch(err => {
                console.error(err)
                setSnackbarData({ open: true, severity: 'error', message: 'Erro ao efetuar download do arquivo!' })
            })
    }

    const handlePaginationChange = useCallback(
        (updater) => {
            const nextPagination =
                typeof updater === "function" ? updater(pagination) : updater

            dispatch(acSetBatchFilesTablePagination(nextPagination))
        },
        [dispatch, pagination]
    )

    // Table Functions
    const renderRowActions = ({ row }) => {

        const id = row.original.id
        const name = row.original.name
        const status = row.original.status

        if (status === 'success') {
            return (
                <Tooltip title='Efetuar Download'>
                    <IconButton onClick={() => handleDownload(id, name)}>
                        <Download />
                    </IconButton>
                </Tooltip>
            )

        }

        return (<></>)
    }

    const renderStatusColumn = (cell) => {
        const status = cell.getValue()
        return (
            <Chip
                label={statusMap[status].message}
                color={statusMap[status].color}
                size='small'
                variant='filled'
            />
        )
    }

    const renderDateColumn = (cell) => {
        const date = dayjs(cell.getValue())
        return date.locale('pt-br').format('DD/MM/YYYY HH:mm')
    }

    const columns = [
        {
            accessorKey: 'id',
            header: 'ID Lote',
            size: 80,
            grow: 0
        },
        {
            accessorKey: 'name',
            header: 'Nome',
            grow: 0
        },
        {
            accessorKey: 'status',
            header: 'Status',
            size: 80,
            grow: 0,
            Cell: ({ cell }) => renderStatusColumn(cell)
        },
        {
            accessorKey: 'message',
            header: 'Mensagem',
            size: 400,
            grow: 1,
            muiTableBodyCellProps: {
                sx: {
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    wordBreak: "break-word"
                }
            }
        },
        {
            accessorKey: 'uploadDate',
            header: 'Data de Upload',
            size: 90,
            grow: 0,
            Cell: ({ cell }) => renderDateColumn(cell)
        },
        {
            accessorKey: 'processingDate',
            header: 'Data de Processamento',
            size: 90,
            grow: 0,
            Cell: ({ cell }) => renderDateColumn(cell)
        }
    ]

    const table = useMaterialReactTable({

        columns,
        data: allBatchFiles || [],
        rowCount,

        localization: MRT_Localization_PT_BR,

        enableRowActions: true,
        positionActionsColumn: 'last',
        renderRowActions: renderRowActions,
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: 'Download'
            }
        },

        manualFiltering: false,
        manualPagination: true,
        manualSorting: false,

        onPaginationChange: handlePaginationChange,

        enablePagination: true,
        enableSorting: false,
        enableColumnFilters: false,
        enableColumnActions: false,
        enableHiding: false,

        enableGlobalFilter: false,
        enableKeyboardShortcuts: false,
        enableTopToolbar: false,

        initialState: {
            pagination,
            density: 'compact',
            sorting: [
                { id: 'createdAt', desc: true }
            ]
        },

        state: {
            isLoading: isLoadingAllBatchFiles,
            isSaving: isUploadingBatchFile,
            showAlertBanner: hasErrorAllBatchFiles,

            pagination
        },

        muiTablePaperProps: () => ({
            elevation: 0
        }),
        muiPaginationProps: {
            rowsPerPageOptions: [5, 10, 15, 20, 25, 30]
        },
        mrtTheme: {
            baseBackgroundColor: '#FFFFFF'
        }
    })

    return (
        <>
            {(!isLoadingAllBatchFiles && !hasBatchFiles) ? (
                <Stack gap={2} alignItems='center' width='100%'>
                    <InsertDriveFileOutlinedIcon color='primary' sx={{ fontSize: 40 }} />

                    <Typography variant='body2'>
                        Não há lotes importados. Você pode adicionar um novo lote na área de upload acima.
                    </Typography>
                </Stack>
            ) : (
                <MaterialReactTable table={table} />
            )}
        </>
    )
}

BatchFilesTable.propTypes = {
    setSnackbarData: PropTypes.func.isRequired
}

export default BatchFilesTable
